/**
 * EN-5191: pseudo-localisation — the one i18n check that works *in English*.
 *
 * Turn it on with `?pseudo=1` (it sticks for the tab) and off with `?pseudo=0`, from a signed-in
 * patientMpower account — see isPseudoLocaleEnabled for why it is restricted. Every string that
 * goes through i18next comes back accented, padded ~40% longer and wrapped in brackets:
 *
 *     "Add measurement"  ->  "[Åðð mêåšûŕêmêñţ~~~~~~]"
 *
 * What each part of that is for:
 *
 *  - **Accents** make a translated string unmistakable. Anything still rendering in plain
 *    readable English is hardcoded — no second language, no translator and no screen-by-screen
 *    comparison needed to see it.
 *  - **Padding** reproduces the length of a real translation. Dutch runs 30-40% longer than
 *    English and German longer still, so a button sized around its English label clips here
 *    exactly as it does in production. Every layout defect in EN-5191 (fixed 90x26 pills, a
 *    `right: 100px` offset that was the pixel width of "Answer") would have shown up the day
 *    the button was written.
 *  - **Brackets** mark the boundaries. Two keys concatenated read as "[…][…]", and a sentence
 *    assembled from a key plus a literal reads half-transformed — both are visible at a glance
 *    rather than after someone reasons about word order.
 *
 * Deliberately NOT gated on NODE_ENV: CRA sets it to "production" for every `npm run build:*`,
 * including development and staging, so that gate would disable this exactly where the testing
 * happens. It costs a query parameter and only rewrites text.
 *
 * Interpolation placeholders and <Trans> tags are left intact — see transform().
 */

import { isPMPUser } from "./isPMPUser";

export const PSEUDO_STORAGE_KEY = "i18nPseudo";

/**
 * U+3010 / U+3011. Deliberately NOT the ⟦⟧ that i18nSourceDebug uses: both aids can be on at
 * once, and sharing a marker would double-wrap the text and make the source highlighter colour
 * pseudo strings as though they had come from the API.
 */
export const PSEUDO_START = "【";
export const PSEUDO_END = "】";

/** How much longer than the source a real translation is assumed to run. */
export const PSEUDO_EXPANSION = 0.4;

const PSEUDO_PAD_CHAR = "~";

const ACCENTS: Record<string, string> = {
  a: "å",
  b: "ƀ",
  c: "ç",
  d: "ð",
  e: "ê",
  f: "ƒ",
  g: "ĝ",
  h: "ĥ",
  i: "î",
  j: "ĵ",
  k: "ķ",
  l: "ļ",
  m: "m",
  n: "ñ",
  o: "ö",
  p: "þ",
  q: "q",
  r: "ŕ",
  s: "š",
  t: "ţ",
  u: "û",
  v: "v",
  w: "ŵ",
  x: "x",
  y: "ý",
  z: "ž",
  A: "Å",
  B: "Ɓ",
  C: "Ç",
  D: "Ð",
  E: "Ê",
  F: "Ƒ",
  G: "Ĝ",
  H: "Ĥ",
  I: "Î",
  J: "Ĵ",
  K: "Ķ",
  L: "Ļ",
  M: "M",
  N: "Ñ",
  O: "Ö",
  P: "Þ",
  Q: "Q",
  R: "Ŕ",
  S: "Š",
  T: "Ţ",
  U: "Û",
  V: "V",
  W: "Ŵ",
  X: "X",
  Y: "Ý",
  Z: "Ž",
};

/**
 * Spans that must survive untouched, or the string stops working rather than merely looking
 * odd: i18next interpolation (`{{count}}`, `{{ward}}`), its nesting syntax (`$t(other_key)`)
 * and the <Trans> component tags (`<bold>…</bold>`). Accenting the name inside `{{ }}` would
 * break the lookup and render the raw placeholder; accenting a tag name would drop the markup.
 */
const PRESERVED = /(\{\{[^}]*\}\}|\$t\([^)]*\)|<\/?[A-Za-z][\w-]*\s*\/?>)/g;

/**
 * Decided once per page load and reused. The flag is read from two places — the baseline
 * bundle when config/i18n.ts initialises, and the published bundle every time it lands — and
 * the answer must be the same for both. It is not naturally stable: signing in changes
 * isPMPUser() mid-session, which would leave the baseline plain and the published copy
 * pseudo-localised, so a baseline-only string would look hardcoded when it is not. One
 * evaluation, one answer; turning the aid on after signing in means reloading, which is what
 * adding `?pseudo=1` to the URL does anyway.
 */
let cachedDecision: boolean | undefined;

/** Test seam. Nothing in the app should call this — the value is meant to be fixed. */
export function resetPseudoLocaleCache(): void {
  cachedDecision = undefined;
}

function readFlag(): boolean {
  try {
    const param = new URLSearchParams(window.location.search).get("pseudo");

    if (param === "1") {
      window.sessionStorage.setItem(PSEUDO_STORAGE_KEY, "1");
      return true;
    }

    if (param === "0") {
      window.sessionStorage.removeItem(PSEUDO_STORAGE_KEY);
      return false;
    }

    return window.sessionStorage.getItem(PSEUDO_STORAGE_KEY) === "1";
  } catch {
    // Private mode, or storage blocked. A debug aid is never worth breaking a render for.
    return false;
  }
}

function clearFlag(): void {
  try {
    window.sessionStorage.removeItem(PSEUDO_STORAGE_KEY);
  } catch {
    // Nothing to do: the flag is already unreachable, which is the state we wanted.
  }
}

/**
 * Reads the flag, letting `?pseudo=1|0` set or clear the stored preference first so the URL
 * always beats what a previous visit left behind.
 *
 * **Restricted to patientMpower accounts.** This aid makes the whole portal unreadable, so a
 * link carrying `?pseudo=1` must not do anything in a customer's hands — pasted into a support
 * thread, forwarded, or clicked from a bookmark on a shared machine. A request from anyone
 * else is refused *and* the stored flag cleared, so it cannot sit in the tab waiting for the
 * next person to sign in.
 *
 * Signed out counts as "anyone else": there is no account to check, and the login screen is
 * exactly where a stray link would land.
 */
export function isPseudoLocaleEnabled(): boolean {
  if (cachedDecision !== undefined) return cachedDecision;

  const requested = readFlag();

  if (requested && !isPMPUser()) {
    clearFlag();
    cachedDecision = false;
    return false;
  }

  cachedDecision = requested;
  return requested;
}

function accent(text: string): string {
  return text.replace(
    /[A-Za-z]/g,
    (character) => ACCENTS[character] ?? character
  );
}

/**
 * Accents the letters, pads to the expansion ratio and brackets the result, leaving every
 * preserved span exactly as it was.
 *
 * Padding is measured on the visible text only: placeholders are replaced at render time by
 * values this function never sees, so counting their braces would inflate short strings and
 * under-pad long ones.
 */
export function pseudoLocalise(value: string): string {
  if (!value) return value;

  const parts = value.split(PRESERVED);
  const accented = parts
    .map((part, index) => (index % 2 === 1 ? part : accent(part)))
    .join("");

  const visibleLength = parts
    .filter((_, index) => index % 2 === 0)
    .join("").length;
  const padding = PSEUDO_PAD_CHAR.repeat(
    Math.ceil(visibleLength * PSEUDO_EXPANSION)
  );

  return `${PSEUDO_START}${accented}${padding}${PSEUDO_END}`;
}

/**
 * Returns a copy of an i18next resource tree with every leaf pseudo-localised. The input is
 * never mutated: it is the bundle imported as a module and the object React Query has cached,
 * and transforming either in place would double-wrap on the next render.
 */
export function pseudoLocaliseResources(resources: unknown): unknown {
  if (typeof resources === "string") return pseudoLocalise(resources);

  if (resources === null || typeof resources !== "object") return resources;

  return Object.fromEntries(
    Object.entries(resources as Record<string, unknown>).map(([key, value]) => [
      key,
      pseudoLocaliseResources(value),
    ])
  );
}
